<template>
  <div class="modal-overlay">
    <div class="modal-container">
      <div class="modal-header" :class="{ loading: loadingInvoices }">
        <h5>Invoice Summary</h5>

        <!-- Date Picker -->
        <div class="text-center date-picker-container">
          <label for="fromDate">From:&nbsp;</label>
          <input
              type="date"
              id="fromDate"
              v-model="fromDate"
              :max="todayDate"
              class="styled-date-picker"
          />
          &nbsp;&nbsp;
          <label for="toDate">To:&nbsp;</label>
          <input
              type="date"
              id="toDate"
              v-model="toDate"
              :max="todayDate"
              class="styled-date-picker"
          />
          &nbsp;&nbsp;
          <button @click="fetchInvoices" class="btn btn-light search-btn">
            <i class="fa-solid fa-magnifying-glass"></i>
            <span class="tooltip-text">Search</span>
          </button>
        </div>

        <!-- Close Button -->
        <button class="btn btn-light close-btn" @click="closeModal">
          <i class="fa-solid fa-xmark"></i>
          <span class="tooltip-text">Close</span>
        </button>
      </div>

      <!-- Embed Google Maps, Leaflet, or any Map Component -->
      <div class="modal-body">
        <div id="map-container">
          <div v-if="filteredInvoices.length === 0 && !loadingInvoices" class="no-invoices">
            <img src="../../../assets/vectors/data_not_found.png" alt="No Invoices" class="no-invoices-image" />
            <h3 class="text-center text-danger">No invoices found!</h3>
            <p class="text-muted">It looks like there are no any invoices for selected date range.</p>
          </div>
          <div v-else ref="mapContainer" class="map"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { showInvoicesOnMap } from "@/services/dataService";

export default {
  name: 'InvoiceMapModal',
  props: {
    profileId: {
      type: String,
      default: ''
    }
  },

  emits: ['close-modal'],

  data() {
    return {
      //Get the API access token from the local storage
      token: localStorage.getItem('token_set'), // Get token from localStorage

      loadingInvoices: false,
      filteredInvoices: [],
      fromDate: new Date().toISOString().split('T')[0], // Default to today
      toDate: new Date().toISOString().split('T')[0], // Default to today
      todayDate: new Date().toISOString().split('T')[0], // Block future dates
    };
  },

  watch: {
    filteredInvoices: {
      handler(newInvoices) {
        if (newInvoices.length > 0) {
          this.$nextTick(() => {
            this.initMap();
          });
        }
      },
      immediate: true
    }
  },

  mounted() {
    this.enterFullScreen();
    this.loadGoogleMaps();
    this.fetchInvoices(); // Fetch invoices when modal opens
  },

  beforeUnmount() {
    this.exitFullScreen();
  },

  methods: {
    //View Full Screen Web Page
    enterFullScreen() {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.mozRequestFullScreen) { // Firefox
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) { // Chrome, Safari, Edge
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) { // IE/Edge
        elem.msRequestFullscreen();
      }
    },

    //Exit Full Screen Web Page
    exitFullScreen() {
      if (document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) { // Firefox
          document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) { // Chrome, Safari, Edge
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { // IE/Edge
          document.msExitFullscreen();
        }
      }
    },

    //Close modal
    closeModal() {
      this.$emit('close-modal');
    },

    //Fetch Invoices for selected date
    async fetchInvoices() {
      try {
        this.loadingInvoices = true;  // Set loading state
        this.filteredInvoices = []; // Reset the filtered Invoice Array

        // Convert fromDate and toDate to Date objects for comparison
        const fromDateObj = new Date(this.fromDate);
        const toDateObj = new Date(this.toDate);

        // Validate date range
        if (toDateObj < fromDateObj) {
          this.$toast.error("Invalid Date Range.");
          this.loadingInvoices = false;
          return;
        }

        //Pass the parameters to the API payload
        const payload = {
          from_date: this.fromDate,
          to_date: this.toDate,
          profile_id: this.profileId
        };

        //Call the API for fetch data
        const response = await showInvoicesOnMap(payload, this.token);

        // Fetch the invoices data with location
        if (response.data.all && Array.isArray(response.data.all)) {
          this.filteredInvoices = response.data.all.map(invoice => ({
            name: invoice.name,
            createdDateTime: invoice.date,
            invoiceNo: invoice.number,
            invoiceAmount: invoice.amount,
            collectedAmount: invoice.collected_amount ? invoice.collected_amount : "Rs.0.00",
            duePayments: invoice.due_amount ? invoice.due_amount : "Rs.0.00",
            address: invoice.address,
            coords: {
              lat: parseFloat(invoice.coords.lat),
              lng: parseFloat(invoice.coords.lng),
            },
          }));
        }

      } catch (e) {
        console.error(e);
        this.$toast.error("Failed to fetch invoices. Please try again.");
      } finally {
        this.loadingInvoices = false;
      }
    },

    //Call the Google Map API with key
    loadGoogleMaps() {
      if (window.google && window.google.maps) {
        this.initMap();
      } else {
        const script = document.createElement("script");
        const googleKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;

        script.src = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&callback=initMap`;
        script.defer = true;
        script.async = true;
        script.onload = () => this.initMap();
        document.head.appendChild(script);
      }
    },

    //Passing the data to the map to point invoices and tooltip
    initMap() {
      if (!this.$refs.mapContainer || this.filteredInvoices.length === 0) return;

      const map = new google.maps.Map(this.$refs.mapContainer, {
        center: { lat: 7.8731, lng: 80.7718 }, // Sri Lanka center
        zoom: 7
      });

      //Drawing the directions with points
      const directionsService = new google.maps.DirectionsService();
      const directionsRenderer = new google.maps.DirectionsRenderer({
        suppressMarkers: true, // We'll use custom markers
        polylineOptions: {
          strokeColor: "#211C84", // Change to any HEX or RGB color
          strokeOpacity: 1.0,
          strokeWeight: 5, // Line thickness
        },
      });
      directionsRenderer.setMap(map);

      //Points properties defining
      this.filteredInvoices.forEach((invoice) => {
        const marker = new google.maps.Marker({
          position: invoice.coords,
          map: map,
          draggable: false, // Enable route dragging
          title: `(${invoice.coords.lat},${invoice.coords.lng})`,
        });

        const infoWindow = new google.maps.InfoWindow({
          disableAutoPan: true,
          content: `
            <div class="dealer-info">
              <span style="font-weight: 600">${invoice.name}</span><br>
              ${invoice.address}<br>
              Invoice No: ${invoice.invoiceNo}<br>
              ${invoice.createdDateTime}<br>
              Invoice Amount: ${invoice.invoiceAmount}<br>
              <span style="color: green;">Paid: ${invoice.collectedAmount}</span><br>
              <span style="color: red;">Due: ${invoice.duePayments}</span>
            </div>
          `,
        });

        // Define route points
        const waypoints = this.filteredInvoices.slice(1, -1).map(invoice => ({
          location: invoice.coords,
          stopover: true
        }));

        const request = {
          origin: this.filteredInvoices[0].coords,   // Start point
          destination: this.filteredInvoices[this.filteredInvoices.length - 1].coords, // End point
          waypoints,
          optimizeWaypoints: true,  // Let Google optimize the best route
          travelMode: google.maps.TravelMode.DRIVING,
        };

        // Request directions
        directionsService.route(request, (result, status) => {
          if (status === google.maps.DirectionsStatus.OK) {
            directionsRenderer.setDirections(result);
          } else {
            console.error("Directions request failed due to " + status);
          }
        });

        //Show the tooltip when mouse hover the info window
        marker.addListener("mouseover", () => {
          infoWindow.open(map, marker);

          //Hide the close button in the info window
          setTimeout(() => {
            // Remove the 'x' button after the InfoWindow opens
            const closeButton = document.querySelectorAll('.gm-ui-hover-effect')[0];
            if (closeButton) {
              closeButton.style.display = 'none';
            }
          }, 10); // Wait a little for the InfoWindow to open
        });

        //Hide the info window when mouse out from the point
        marker.addListener("mouseout", () => {
          infoWindow.close();
        });
      });
    },
  }
}
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* Modal Container */
.modal-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 1000px;
  max-width: 90%;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
}

/* Progress Bar Effect */
.modal-header.loading {
  position: relative;
  border-bottom: none; /* Hide static border */
}

.modal-header.loading::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 3px;
  background: linear-gradient(to right, #0d6efd, #28a745, #f4b400, #dc3545);
  animation: loadingBar 1.5s linear infinite;
}

@keyframes loadingBar {
  0% {
    width: 0;
  }
  50% {
    width: 50%;
  }
  100% {
    width: 100%;
  }
}

/* Close Button */
.close-btn {
  border-radius: 50%; /* Makes the button circular */
  width: 33px; /* Set a fixed width */
  height: 33px; /* Match the tab height */
  padding: 0;
  cursor: pointer;
}

/* Map Container */
#map-container {
  width: 100%;
  height: 450px;
  background: #f4f4f4;
  border-radius: 8px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.map {
  width: 100%;
  height: 100%;
}

.no-invoices {
  text-align: center;
  color: #666;
}

.no-invoices p {
  font-size: 14px;
  font-weight: lighter;
}

.no-invoices-image {
  max-width: 175px;
  margin-bottom: 10px;
}

.styled-date-picker {
  padding: 5px 10px;
  font-size: 14px;
  border: 1px solid #CCCCCC; /* Blue border */
  border-radius: 6px;
  outline: none;
  background-color: #fff;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
}

.styled-date-picker:hover {
  border-color: #666; /* Darker blue on hover */
}

.styled-date-picker:focus {
  border-color: #666;
}

.styled-date-picker::-webkit-calendar-picker-indicator {
  filter: invert(30%);
  cursor: pointer;
}

.styled-date-picker:disabled {
  background-color: #f0f0f0;
  cursor: not-allowed;
}

.search-btn {
  border-radius: 5px; /* Makes the button circular */
  width: 33px; /* Set a fixed width */
  height: 33px; /* Match the tab height */
  padding: 0;
}

/* Tooltip Styling */
.tooltip-text {
  visibility: hidden;
  background-color: #333;
  color: #fff;
  text-align: center;
  padding: 6px 10px;
  border-radius: 4px;
  position: absolute;
  bottom: 40px; /* Distance above the button */
  right: 50%;
  transform: translateX(50%);
  white-space: nowrap;
  font-size: 12px;
  z-index: 1001;
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* Show tooltip on hover */
.search-btn:hover .tooltip-text,
.close-btn:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
</style>